import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FilterUsers } from "@gpstrackit/gtc-react-controls";
import { getDriversSearch, initializeInstance, removeInstance } from "./redux/slice";
import { v4 as uuidv4 } from 'uuid';
import { useIntl } from 'react-intl';
// Constants
const LIMIT = 25;
const DEFAULT_AVATAR = "";

/**
 * FilterAvatars Component
 * 
 * A component that displays a filterable list of driver avatars with search and pagination capabilities.
 * Each instance maintains its own independent state in Redux.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.selectedDrivers - Currently selected drivers
 * @param {Function} props.setSelectedDrivers - Function to update selected drivers
 */
export const FilterAvatars = (props) => {
    const {
        selectedDrivers,
        setSelectedDrivers,
    } = props;

    const { messages } = useIntl();

    // State for panel visibility
    const [isShowVisible, setIsShowVisible] = useState(false);

    // Generate a unique ID for this component instance
    const [instanceId] = useState(() => uuidv4());

    // Get Redux dispatch function
    const dispatch = useDispatch();

    // Get total drivers count from Redux state for this instance
    const { totalDrivers = 0 } = useSelector(state =>
        state.filterAvatarsRedux.instances[instanceId] || { totalDrivers: 0 }
    );

    // Local state for pagination
    const [offset, setOffset] = useState(0);
    const [allDrivers, setAllDrivers] = useState([]);

    // Reference to store initial drivers
    const initialDriversRef = useRef(null);

    // Flag to indicate if initial load is complete
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    // Error state
    const [error, setError] = useState(null);

    /**
     * Initialize and cleanup instance in Redux
     */
    useEffect(() => {
        // Initialize instance in Redux
        dispatch(initializeInstance({ instanceId }));

        // Cleanup when component unmounts
        return () => {
            dispatch(removeInstance({ instanceId }));
        };
    }, [dispatch, instanceId]);

    /**
     * Format driver data for display
     * @param {Array} drivers - Raw driver data from API
     * @returns {Array} Formatted driver data
     */
    const formatDrivers = useCallback((drivers) => {
        return drivers.map(driver => ({
            id: driver.id,
            name: driver.title || driver.name,
            avatar: driver.avatarUrl || '',
            isActive: driver.status == 1
        }));
    }, []);

    /**
     * Load initial drivers when component mounts
     */
    useEffect(() => {
        setError(null);

        dispatch(getDriversSearch({
            instanceId,
            conditions: [],
            sort: [],
            limit: LIMIT,
            offset: 0
        }))
            .then((action) => {
                if (action.payload?.data?.items) {
                    const formattedDrivers = formatDrivers(action.payload.data.items);
                    setAllDrivers(formattedDrivers);

                    // Store initial drivers in ref
                    initialDriversRef.current = formattedDrivers.map(driver => ({
                        ...driver,
                        avatar: driver.avatar || DEFAULT_AVATAR
                    }));

                    setOffset(LIMIT);
                    setInitialLoadComplete(true);
                } else {
                    setError("No drivers found");
                }
            })
            .catch(err => {
                console.error("Failed to load initial drivers:", err);
                setError("Failed to load drivers. Please try again later.");
            });
    }, [dispatch, instanceId, formatDrivers]);

    /**
     * Load more drivers for infinite scrolling
     * @returns {Promise<Object>} Object containing new users and hasMore flag
     */
    const fetchMoreUsers = useCallback(async () => {
        try {
            setError(null);

            const result = await dispatch(getDriversSearch({
                instanceId,
                conditions: [],
                sort: [],
                limit: LIMIT,
                offset: offset
            })).unwrap();

            const newItems = result.data.items || [];
            const formattedNewItems = formatDrivers(newItems);

            // Update state with new drivers
            setAllDrivers(prev => [...prev, ...formattedNewItems]);
            setOffset(prev => prev + LIMIT);

            // Check if there are more drivers to load
            const total = result.data.total || 0;
            const hasMore = (offset + LIMIT) < total;

            return {
                users: formattedNewItems,
                hasMore
            };
        } catch (error) {
            console.error("Error loading more drivers:", error);
            setError("Failed to load more drivers. Please try again later.");
            return {
                users: [],
                hasMore: false
            };
        }
    }, [dispatch, offset, instanceId, formatDrivers]);

    /**
     * Search for drivers based on query
     * @param {string} query - Search query
     * @returns {Promise<Object>} Object containing matching users and hasMore flag
     */
    const handleSearchUsers = useCallback(async (query) => {
        try {
            setError(null);

            const result = await dispatch(getDriversSearch({
                instanceId,
                conditions: [{
                    fields: ["title"],
                    terms: [query],
                    exact: false
                }],
                sort: [],
                limit: 100,
                offset: 0
            })).unwrap();

            const formattedDrivers = formatDrivers(result.data.items || []);

            // Update state with search results
            setAllDrivers(formattedDrivers);
            setOffset(LIMIT);

            // Return results in format expected by FilterUsers component
            return {
                users: formattedDrivers,
                hasMore: false // Since we're loading all results at once with limit: 100
            };
        } catch (error) {
            console.error("Error searching drivers:", error);
            setError("Failed to search drivers. Please try again later.");
            return {
                users: [],
                hasMore: false
            };
        }
    }, [dispatch, instanceId, formatDrivers]);

    // Use initial drivers for the list
    const driversToShow = useMemo(() => {
        return initialLoadComplete ? initialDriversRef.current : [];
    }, [initialLoadComplete]);

    return (
        <div>
            {error && (
                <div className="error-message" style={{ color: 'red', marginBottom: '10px' }}>
                    {error}
                </div>
            )}
            <FilterUsers
                usersList={driversToShow}
                selectedUsers={selectedDrivers}
                onSelectUsers={setSelectedDrivers}
                surplus={selectedDrivers.length > 0 ? selectedDrivers.length : totalDrivers}
                max={4}
                isShowVisible={isShowVisible}
                onClickShowMore={() => setIsShowVisible(!isShowVisible)}
                fetchMoreUsers={fetchMoreUsers}
                searchUsers={handleSearchUsers}
                messageLoading={messages['filterAvatars_loading']}
                placeholderInputSearch={messages['filterAvatars_search_drivers']}
            />
        </div>
    );
};